import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule, SkipSelf } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpContext, HttpParams } from '@angular/common/http';

/**
 * Custom HttpParameterCodec
 * Workaround for https://github.com/angular/angular/issues/18261
 */
class CustomHttpParameterCodec {
  encodeKey(k) {
    return encodeURIComponent(k);
  }
  encodeValue(v) {
    return encodeURIComponent(v);
  }
  decodeKey(k) {
    return decodeURIComponent(k);
  }
  decodeValue(v) {
    return decodeURIComponent(v);
  }
}
const BASE_PATH = new InjectionToken('basePath');
const COLLECTION_FORMATS = {
  'csv': ',',
  'tsv': '   ',
  'ssv': ' ',
  'pipes': '|'
};
class Configuration {
  /**
   *  @deprecated Since 5.0. Use credentials instead
   */
  apiKeys;
  username;
  password;
  /**
   *  @deprecated Since 5.0. Use credentials instead
   */
  accessToken;
  basePath;
  withCredentials;
  /**
   * Takes care of encoding query- and form-parameters.
   */
  encoder;
  /**
   * Encoding of various path parameter
   * <a href="https://github.com/OAI/OpenAPI-Specification/blob/main/versions/3.1.0.md#style-values">styles</a>.
   * <p>
   * See {@link README.md} for more details
   * </p>
   */
  encodeParam;
  /**
   * The keys are the names in the securitySchemes section of the OpenAPI
   * document. They should map to the value used for authentication
   * minus any standard prefixes such as 'Basic' or 'Bearer'.
   */
  credentials;
  constructor(configurationParameters = {}) {
    this.apiKeys = configurationParameters.apiKeys;
    this.username = configurationParameters.username;
    this.password = configurationParameters.password;
    this.accessToken = configurationParameters.accessToken;
    this.basePath = configurationParameters.basePath;
    this.withCredentials = configurationParameters.withCredentials;
    this.encoder = configurationParameters.encoder;
    if (configurationParameters.encodeParam) {
      this.encodeParam = configurationParameters.encodeParam;
    } else {
      this.encodeParam = param => this.defaultEncodeParam(param);
    }
    if (configurationParameters.credentials) {
      this.credentials = configurationParameters.credentials;
    } else {
      this.credentials = {};
    }
    // init default jwtAuth credential
    if (!this.credentials['jwtAuth']) {
      this.credentials['jwtAuth'] = () => {
        return typeof this.accessToken === 'function' ? this.accessToken() : this.accessToken;
      };
    }
  }
  /**
   * Select the correct content-type to use for a request.
   * Uses {@link Configuration#isJsonMime} to determine the correct content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param contentTypes - the array of content types that are available for selection
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderContentType(contentTypes) {
    if (contentTypes.length === 0) {
      return undefined;
    }
    const type = contentTypes.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return contentTypes[0];
    }
    return type;
  }
  /**
   * Select the correct accept content-type to use for a request.
   * Uses {@link Configuration#isJsonMime} to determine the correct accept content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param accepts - the array of content types that are available for selection.
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderAccept(accepts) {
    if (accepts.length === 0) {
      return undefined;
    }
    const type = accepts.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return accepts[0];
    }
    return type;
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    const jsonMime = new RegExp('^(application\/json|[^;/ \t]+\/[^;/ \t]+[+]json)[ \t]*(;.*)?$', 'i');
    return mime !== null && (jsonMime.test(mime) || mime.toLowerCase() === 'application/json-patch+json');
  }
  lookupCredential(key) {
    const value = this.credentials[key];
    return typeof value === 'function' ? value() : value;
  }
  defaultEncodeParam(param) {
    // This implementation exists as fallback for missing configuration
    // and for backwards compatibility to older typescript-angular generator versions.
    // It only works for the 'simple' parameter style.
    // Date-handling only works for the 'date-time' format.
    // All other styles and Date-formats are probably handled incorrectly.
    //
    // But: if that's all you need (i.e.: the most common use-case): no need for customization!
    const value = param.dataFormat === 'date-time' && param.value instanceof Date ? param.value.toISOString() : param.value;
    return encodeURIComponent(String(value));
  }
}

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class ApiService {
  httpClient;
  basePath = 'http://localhost';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  apiTokenBlacklistCreate(tokenBlacklistRequest, observe = 'body', reportProgress = false, options) {
    if (tokenBlacklistRequest === null || tokenBlacklistRequest === undefined) {
      throw new Error('Required parameter tokenBlacklistRequest was null or undefined when calling apiTokenBlacklistCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = [];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/api/token/blacklist/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: tokenBlacklistRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  apiTokenVerifyCreate(tokenVerifyRequest, observe = 'body', reportProgress = false, options) {
    if (tokenVerifyRequest === null || tokenVerifyRequest === undefined) {
      throw new Error('Required parameter tokenVerifyRequest was null or undefined when calling apiTokenVerifyCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = [];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/api/token/verify/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: tokenVerifyRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function ApiService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ApiService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ApiService,
    factory: ApiService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ApiService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class AuthService {
  httpClient;
  basePath = 'http://localhost';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  authTokenCreate(jwtObtainPairRequest, observe = 'body', reportProgress = false, options) {
    if (jwtObtainPairRequest === null || jwtObtainPairRequest === undefined) {
      throw new Error('Required parameter jwtObtainPairRequest was null or undefined when calling authTokenCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = [];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/auth/token/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: jwtObtainPairRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  authTokenRefreshCreate(tokenRefreshRequest, observe = 'body', reportProgress = false, options) {
    if (tokenRefreshRequest === null || tokenRefreshRequest === undefined) {
      throw new Error('Required parameter tokenRefreshRequest was null or undefined when calling authTokenRefreshCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/auth/token/refresh/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: tokenRefreshRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function AuthService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || AuthService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: AuthService,
    factory: AuthService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class MainProfileService {
  httpClient;
  basePath = 'http://localhost';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  mainProfileMainProfileCommunityApplicationSettingsCreate(postUserCommonApplicationProfileSettingsRequest, observe = 'body', reportProgress = false, options) {
    if (postUserCommonApplicationProfileSettingsRequest === null || postUserCommonApplicationProfileSettingsRequest === undefined) {
      throw new Error('Required parameter postUserCommonApplicationProfileSettingsRequest was null or undefined when calling mainProfileMainProfileCommunityApplicationSettingsCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_profile_community_application_settings`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postUserCommonApplicationProfileSettingsRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  mainProfileMainProfileCommunityApplicationSettingsRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_profile_community_application_settings`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  mainProfileMainProfilePrivacyCreate(updatePrivacyRequestRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_profile_privacy`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: updatePrivacyRequestRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  mainProfileMainProfileSettingsCreate(changeProfileRequestRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_profile_settings`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: changeProfileRequestRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  mainProfileMainProfileSettingsRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_profile_settings`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  mainProfileMainPublicInformationRetrieve(userId, username, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (userId !== undefined && userId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
    }
    if (username !== undefined && username !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, username, 'username');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_public_information`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  mainProfileMainPublicProfileRetrieve(businessId, userId, username, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (userId !== undefined && userId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
    }
    if (username !== undefined && username !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, username, 'username');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_public_profile`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function MainProfileService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || MainProfileService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: MainProfileService,
    factory: MainProfileService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MainProfileService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class UserService {
  httpClient;
  basePath = 'http://localhost';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  userAddressBookCreate(addressBookRequest, observe = 'body', reportProgress = false, options) {
    if (addressBookRequest === null || addressBookRequest === undefined) {
      throw new Error('Required parameter addressBookRequest was null or undefined when calling userAddressBookCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/address_book`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: addressBookRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userAddressBookDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling userAddressBookDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/address_book`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userAddressBookList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/address_book`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userDeleteUserCreate(userDeleteRequest, observe = 'body', reportProgress = false, options) {
    if (userDeleteRequest === null || userDeleteRequest === undefined) {
      throw new Error('Required parameter userDeleteRequest was null or undefined when calling userDeleteUserCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/delete_user`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: userDeleteRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userGetcommondataRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/getcommondata`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userIndicatorsRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/indicators/`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userLoginCreate(loginRequest, observe = 'body', reportProgress = false, options) {
    if (loginRequest === null || loginRequest === undefined) {
      throw new Error('Required parameter loginRequest was null or undefined when calling userLoginCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/login/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: loginRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userLogoutCreate(logoutRequest, observe = 'body', reportProgress = false, options) {
    if (logoutRequest === null || logoutRequest === undefined) {
      throw new Error('Required parameter logoutRequest was null or undefined when calling userLogoutCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/logout`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: logoutRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userPasswordchangeCreate(changeUserPasswordRequest, observe = 'body', reportProgress = false, options) {
    if (changeUserPasswordRequest === null || changeUserPasswordRequest === undefined) {
      throw new Error('Required parameter changeUserPasswordRequest was null or undefined when calling userPasswordchangeCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/passwordchange`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: changeUserPasswordRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userRegistrationCreate(registrationRequest, observe = 'body', reportProgress = false, options) {
    if (registrationRequest === null || registrationRequest === undefined) {
      throw new Error('Required parameter registrationRequest was null or undefined when calling userRegistrationCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/registration/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: registrationRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userRegistrationPasswordresetCreate(passwordResetRequest, observe = 'body', reportProgress = false, options) {
    if (passwordResetRequest === null || passwordResetRequest === undefined) {
      throw new Error('Required parameter passwordResetRequest was null or undefined when calling userRegistrationPasswordresetCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/registration/passwordreset`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: passwordResetRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userRegistrationResendCreate(emialResendRequest, observe = 'body', reportProgress = false, options) {
    if (emialResendRequest === null || emialResendRequest === undefined) {
      throw new Error('Required parameter emialResendRequest was null or undefined when calling userRegistrationResendCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/registration/resend`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: emialResendRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userSocialLoginCreate(loginWithSocialMediaRequest, observe = 'body', reportProgress = false, options) {
    if (loginWithSocialMediaRequest === null || loginWithSocialMediaRequest === undefined) {
      throw new Error('Required parameter loginWithSocialMediaRequest was null or undefined when calling userSocialLoginCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/social_login`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: loginWithSocialMediaRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userUsersList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/users`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function UserService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || UserService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: UserService,
    factory: UserService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class UsersService {
  httpClient;
  basePath = 'http://localhost';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  users2faEnableCreate(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/users/2fa/enable/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  users2faVerifyCreate(verify2FARequest, observe = 'body', reportProgress = false, options) {
    if (verify2FARequest === null || verify2FARequest === undefined) {
      throw new Error('Required parameter verify2FARequest was null or undefined when calling users2faVerifyCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = [];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/users/2fa/verify/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: verify2FARequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function UsersService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || UsersService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: UsersService,
    factory: UsersService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UsersService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();
const APIS = [ApiService, AuthService, MainProfileService, UserService, UsersService];
var AddressBookModel;
(function (AddressBookModel) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Personal"] = "personal";
    TypeEnum["Business"] = "business";
  })(TypeEnum = AddressBookModel.TypeEnum || (AddressBookModel.TypeEnum = {}));
})(AddressBookModel || (AddressBookModel = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var AddressBookRequest;
(function (AddressBookRequest) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Personal"] = "personal";
    TypeEnum["Business"] = "business";
  })(TypeEnum = AddressBookRequest.TypeEnum || (AddressBookRequest.TypeEnum = {}));
})(AddressBookRequest || (AddressBookRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ChangeProfileRequestRequest;
(function (ChangeProfileRequestRequest) {
  let GenderEnum;
  (function (GenderEnum) {
    GenderEnum["Unknown"] = "unknown";
    GenderEnum["Male"] = "male";
    GenderEnum["Female"] = "female";
    GenderEnum["Other"] = "other";
  })(GenderEnum = ChangeProfileRequestRequest.GenderEnum || (ChangeProfileRequestRequest.GenderEnum = {}));
})(ChangeProfileRequestRequest || (ChangeProfileRequestRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var CommonApplicationProfileSettingsLocation;
(function (CommonApplicationProfileSettingsLocation) {
  let LocationShowEnum;
  (function (LocationShowEnum) {
    LocationShowEnum["DontShow"] = "dont_show";
    LocationShowEnum["Country"] = "country";
    LocationShowEnum["CountryAndCity"] = "country_and_city";
  })(LocationShowEnum = CommonApplicationProfileSettingsLocation.LocationShowEnum || (CommonApplicationProfileSettingsLocation.LocationShowEnum = {}));
})(CommonApplicationProfileSettingsLocation || (CommonApplicationProfileSettingsLocation = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CommonShortUser;
(function (CommonShortUser) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = CommonShortUser.SubscriptionTypeEnum || (CommonShortUser.SubscriptionTypeEnum = {}));
})(CommonShortUser || (CommonShortUser = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var LoginResponse;
(function (LoginResponse) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = LoginResponse.SubscriptionTypeEnum || (LoginResponse.SubscriptionTypeEnum = {}));
})(LoginResponse || (LoginResponse = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var LoginWithSocialMediaRequest;
(function (LoginWithSocialMediaRequest) {
  let SocialTypeEnum;
  (function (SocialTypeEnum) {
    SocialTypeEnum["Facebook"] = "facebook";
    SocialTypeEnum["Google"] = "google";
    SocialTypeEnum["Apple"] = "apple";
    SocialTypeEnum["Linkedin"] = "linkedin";
    SocialTypeEnum["Twitter"] = "twitter";
    SocialTypeEnum["Vk"] = "vk";
  })(SocialTypeEnum = LoginWithSocialMediaRequest.SocialTypeEnum || (LoginWithSocialMediaRequest.SocialTypeEnum = {}));
})(LoginWithSocialMediaRequest || (LoginWithSocialMediaRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostUserCommonApplicationProfileSettingsRequest;
(function (PostUserCommonApplicationProfileSettingsRequest) {
  let CommunityApplicationTypeEnum;
  (function (CommunityApplicationTypeEnum) {
    CommunityApplicationTypeEnum["LanguageExchange"] = "language_exchange";
    CommunityApplicationTypeEnum["BusinessNetworking"] = "business_networking";
    CommunityApplicationTypeEnum["Dating"] = "dating";
    CommunityApplicationTypeEnum["Garden"] = "garden";
  })(CommunityApplicationTypeEnum = PostUserCommonApplicationProfileSettingsRequest.CommunityApplicationTypeEnum || (PostUserCommonApplicationProfileSettingsRequest.CommunityApplicationTypeEnum = {}));
})(PostUserCommonApplicationProfileSettingsRequest || (PostUserCommonApplicationProfileSettingsRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var Privacy;
(function (Privacy) {
  let WhoFollowEnum;
  (function (WhoFollowEnum) {
    WhoFollowEnum["Private"] = "private";
    WhoFollowEnum["Anybody"] = "anybody";
  })(WhoFollowEnum = Privacy.WhoFollowEnum || (Privacy.WhoFollowEnum = {}));
  let EmailAddressVisibilityEnum;
  (function (EmailAddressVisibilityEnum) {
    EmailAddressVisibilityEnum["Nobody"] = "nobody";
    EmailAddressVisibilityEnum["Anybody"] = "anybody";
    EmailAddressVisibilityEnum["ThoseFollowMe"] = "those_follow_me";
    EmailAddressVisibilityEnum["WhoIFollow"] = "who_i_follow";
  })(EmailAddressVisibilityEnum = Privacy.EmailAddressVisibilityEnum || (Privacy.EmailAddressVisibilityEnum = {}));
  let BirthdayEnum;
  (function (BirthdayEnum) {
    BirthdayEnum["Nobody"] = "nobody";
    BirthdayEnum["Anybody"] = "anybody";
    BirthdayEnum["ThoseFollowMe"] = "those_follow_me";
    BirthdayEnum["WhoIFollow"] = "who_i_follow";
  })(BirthdayEnum = Privacy.BirthdayEnum || (Privacy.BirthdayEnum = {}));
  let InviteToErpEnum;
  (function (InviteToErpEnum) {
    InviteToErpEnum["Nobody"] = "nobody";
    InviteToErpEnum["Anybody"] = "anybody";
    InviteToErpEnum["ThoseFollowMe"] = "those_follow_me";
    InviteToErpEnum["WhoIFollow"] = "who_i_follow";
  })(InviteToErpEnum = Privacy.InviteToErpEnum || (Privacy.InviteToErpEnum = {}));
  let InviteToBusinessEnum;
  (function (InviteToBusinessEnum) {
    InviteToBusinessEnum["Nobody"] = "nobody";
    InviteToBusinessEnum["Anybody"] = "anybody";
    InviteToBusinessEnum["ThoseFollowMe"] = "those_follow_me";
    InviteToBusinessEnum["WhoIFollow"] = "who_i_follow";
  })(InviteToBusinessEnum = Privacy.InviteToBusinessEnum || (Privacy.InviteToBusinessEnum = {}));
  let InviteToCommunityEnum;
  (function (InviteToCommunityEnum) {
    InviteToCommunityEnum["Nobody"] = "nobody";
    InviteToCommunityEnum["Anybody"] = "anybody";
    InviteToCommunityEnum["ThoseFollowMe"] = "those_follow_me";
    InviteToCommunityEnum["WhoIFollow"] = "who_i_follow";
  })(InviteToCommunityEnum = Privacy.InviteToCommunityEnum || (Privacy.InviteToCommunityEnum = {}));
  let MarkOnMediaEnum;
  (function (MarkOnMediaEnum) {
    MarkOnMediaEnum["Nobody"] = "nobody";
    MarkOnMediaEnum["Anybody"] = "anybody";
    MarkOnMediaEnum["ThoseFollowMe"] = "those_follow_me";
    MarkOnMediaEnum["WhoIFollow"] = "who_i_follow";
  })(MarkOnMediaEnum = Privacy.MarkOnMediaEnum || (Privacy.MarkOnMediaEnum = {}));
  let MessageMeEnum;
  (function (MessageMeEnum) {
    MessageMeEnum["Nobody"] = "nobody";
    MessageMeEnum["Anybody"] = "anybody";
    MessageMeEnum["ThoseFollowMe"] = "those_follow_me";
    MessageMeEnum["WhoIFollow"] = "who_i_follow";
  })(MessageMeEnum = Privacy.MessageMeEnum || (Privacy.MessageMeEnum = {}));
  let LocationFindMeEnum;
  (function (LocationFindMeEnum) {
    LocationFindMeEnum["Nobody"] = "nobody";
    LocationFindMeEnum["Anybody"] = "anybody";
    LocationFindMeEnum["ThoseFollowMe"] = "those_follow_me";
    LocationFindMeEnum["WhoIFollow"] = "who_i_follow";
  })(LocationFindMeEnum = Privacy.LocationFindMeEnum || (Privacy.LocationFindMeEnum = {}));
  let LocationShowEnum;
  (function (LocationShowEnum) {
    LocationShowEnum["DontShow"] = "dont_show";
    LocationShowEnum["Country"] = "country";
    LocationShowEnum["CountryAndCity"] = "country_and_city";
  })(LocationShowEnum = Privacy.LocationShowEnum || (Privacy.LocationShowEnum = {}));
})(Privacy || (Privacy = {}));
var ProfileSettings;
(function (ProfileSettings) {
  let GenderEnum;
  (function (GenderEnum) {
    GenderEnum["Unknown"] = "unknown";
    GenderEnum["Male"] = "male";
    GenderEnum["Female"] = "female";
    GenderEnum["Other"] = "other";
  })(GenderEnum = ProfileSettings.GenderEnum || (ProfileSettings.GenderEnum = {}));
})(ProfileSettings || (ProfileSettings = {}));
var ProfileSettingsLocation;
(function (ProfileSettingsLocation) {
  let LocationShowEnum;
  (function (LocationShowEnum) {
    LocationShowEnum["DontShow"] = "dont_show";
    LocationShowEnum["Country"] = "country";
    LocationShowEnum["CountryAndCity"] = "country_and_city";
  })(LocationShowEnum = ProfileSettingsLocation.LocationShowEnum || (ProfileSettingsLocation.LocationShowEnum = {}));
})(ProfileSettingsLocation || (ProfileSettingsLocation = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PublicProfile;
(function (PublicProfile) {
  let IsFollowEnum;
  (function (IsFollowEnum) {
    IsFollowEnum["Block"] = "block";
    IsFollowEnum["Unblock"] = "unblock";
    IsFollowEnum["Follow"] = "follow";
    IsFollowEnum["Pending"] = "pending";
    IsFollowEnum["Reject"] = "reject";
    IsFollowEnum["Accept"] = "accept";
    IsFollowEnum["Delete"] = "delete";
    IsFollowEnum["NotFollow"] = "not_follow";
    IsFollowEnum["Report"] = "report";
    IsFollowEnum["UnReport"] = "un_report";
    IsFollowEnum["Done"] = "done";
    IsFollowEnum["SavedActive"] = "saved_active";
    IsFollowEnum["UnSavedActive"] = "un_saved_active";
    IsFollowEnum["CrmSave"] = "crm_save";
    IsFollowEnum["CrmUnSave"] = "crm_un_save";
    IsFollowEnum["NotRequest"] = "not_request";
    IsFollowEnum["None"] = "none";
    IsFollowEnum["SavedEvent"] = "saved_event";
    IsFollowEnum["UnSavedEvent"] = "un_saved_event";
  })(IsFollowEnum = PublicProfile.IsFollowEnum || (PublicProfile.IsFollowEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = PublicProfile.IsReportedEnum || (PublicProfile.IsReportedEnum = {}));
  let IsBlockedEnum;
  (function (IsBlockedEnum) {
    IsBlockedEnum["Block"] = "block";
    IsBlockedEnum["Unblock"] = "unblock";
    IsBlockedEnum["Follow"] = "follow";
    IsBlockedEnum["Pending"] = "pending";
    IsBlockedEnum["Reject"] = "reject";
    IsBlockedEnum["Accept"] = "accept";
    IsBlockedEnum["Delete"] = "delete";
    IsBlockedEnum["NotFollow"] = "not_follow";
    IsBlockedEnum["Report"] = "report";
    IsBlockedEnum["UnReport"] = "un_report";
    IsBlockedEnum["Done"] = "done";
    IsBlockedEnum["SavedActive"] = "saved_active";
    IsBlockedEnum["UnSavedActive"] = "un_saved_active";
    IsBlockedEnum["CrmSave"] = "crm_save";
    IsBlockedEnum["CrmUnSave"] = "crm_un_save";
    IsBlockedEnum["NotRequest"] = "not_request";
    IsBlockedEnum["None"] = "none";
    IsBlockedEnum["SavedEvent"] = "saved_event";
    IsBlockedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsBlockedEnum = PublicProfile.IsBlockedEnum || (PublicProfile.IsBlockedEnum = {}));
  let IsPendingEnum;
  (function (IsPendingEnum) {
    IsPendingEnum["Block"] = "block";
    IsPendingEnum["Unblock"] = "unblock";
    IsPendingEnum["Follow"] = "follow";
    IsPendingEnum["Pending"] = "pending";
    IsPendingEnum["Reject"] = "reject";
    IsPendingEnum["Accept"] = "accept";
    IsPendingEnum["Delete"] = "delete";
    IsPendingEnum["NotFollow"] = "not_follow";
    IsPendingEnum["Report"] = "report";
    IsPendingEnum["UnReport"] = "un_report";
    IsPendingEnum["Done"] = "done";
    IsPendingEnum["SavedActive"] = "saved_active";
    IsPendingEnum["UnSavedActive"] = "un_saved_active";
    IsPendingEnum["CrmSave"] = "crm_save";
    IsPendingEnum["CrmUnSave"] = "crm_un_save";
    IsPendingEnum["NotRequest"] = "not_request";
    IsPendingEnum["None"] = "none";
    IsPendingEnum["SavedEvent"] = "saved_event";
    IsPendingEnum["UnSavedEvent"] = "un_saved_event";
  })(IsPendingEnum = PublicProfile.IsPendingEnum || (PublicProfile.IsPendingEnum = {}));
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = PublicProfile.SubscriptionTypeEnum || (PublicProfile.SubscriptionTypeEnum = {}));
  let MessageTypeEnum;
  (function (MessageTypeEnum) {
    MessageTypeEnum["Request"] = "rocket_chat_request";
    MessageTypeEnum["Available"] = "rocket_chat_available";
    MessageTypeEnum["Blocked"] = "rocket_chat_blocked";
  })(MessageTypeEnum = PublicProfile.MessageTypeEnum || (PublicProfile.MessageTypeEnum = {}));
})(PublicProfile || (PublicProfile = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var RegistrationRequest;
(function (RegistrationRequest) {
  let SocialTypeEnum;
  (function (SocialTypeEnum) {
    SocialTypeEnum["Facebook"] = "facebook";
    SocialTypeEnum["Google"] = "google";
    SocialTypeEnum["Apple"] = "apple";
    SocialTypeEnum["Linkedin"] = "linkedin";
    SocialTypeEnum["Twitter"] = "twitter";
    SocialTypeEnum["Vk"] = "vk";
  })(SocialTypeEnum = RegistrationRequest.SocialTypeEnum || (RegistrationRequest.SocialTypeEnum = {}));
})(RegistrationRequest || (RegistrationRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var UpdatePrivacyRequestRequest;
(function (UpdatePrivacyRequestRequest) {
  let WhoFollowEnum;
  (function (WhoFollowEnum) {
    WhoFollowEnum["Private"] = "private";
    WhoFollowEnum["Anybody"] = "anybody";
  })(WhoFollowEnum = UpdatePrivacyRequestRequest.WhoFollowEnum || (UpdatePrivacyRequestRequest.WhoFollowEnum = {}));
  let EmailAddressVisibilityEnum;
  (function (EmailAddressVisibilityEnum) {
    EmailAddressVisibilityEnum["Nobody"] = "nobody";
    EmailAddressVisibilityEnum["Anybody"] = "anybody";
    EmailAddressVisibilityEnum["ThoseFollowMe"] = "those_follow_me";
    EmailAddressVisibilityEnum["WhoIFollow"] = "who_i_follow";
  })(EmailAddressVisibilityEnum = UpdatePrivacyRequestRequest.EmailAddressVisibilityEnum || (UpdatePrivacyRequestRequest.EmailAddressVisibilityEnum = {}));
  let BirthdayEnum;
  (function (BirthdayEnum) {
    BirthdayEnum["Nobody"] = "nobody";
    BirthdayEnum["Anybody"] = "anybody";
    BirthdayEnum["ThoseFollowMe"] = "those_follow_me";
    BirthdayEnum["WhoIFollow"] = "who_i_follow";
  })(BirthdayEnum = UpdatePrivacyRequestRequest.BirthdayEnum || (UpdatePrivacyRequestRequest.BirthdayEnum = {}));
  let InviteToErpEnum;
  (function (InviteToErpEnum) {
    InviteToErpEnum["Nobody"] = "nobody";
    InviteToErpEnum["Anybody"] = "anybody";
    InviteToErpEnum["ThoseFollowMe"] = "those_follow_me";
    InviteToErpEnum["WhoIFollow"] = "who_i_follow";
  })(InviteToErpEnum = UpdatePrivacyRequestRequest.InviteToErpEnum || (UpdatePrivacyRequestRequest.InviteToErpEnum = {}));
  let InviteToCommunityEnum;
  (function (InviteToCommunityEnum) {
    InviteToCommunityEnum["Nobody"] = "nobody";
    InviteToCommunityEnum["Anybody"] = "anybody";
    InviteToCommunityEnum["ThoseFollowMe"] = "those_follow_me";
    InviteToCommunityEnum["WhoIFollow"] = "who_i_follow";
  })(InviteToCommunityEnum = UpdatePrivacyRequestRequest.InviteToCommunityEnum || (UpdatePrivacyRequestRequest.InviteToCommunityEnum = {}));
  let MarkOnMediaEnum;
  (function (MarkOnMediaEnum) {
    MarkOnMediaEnum["Nobody"] = "nobody";
    MarkOnMediaEnum["Anybody"] = "anybody";
    MarkOnMediaEnum["ThoseFollowMe"] = "those_follow_me";
    MarkOnMediaEnum["WhoIFollow"] = "who_i_follow";
  })(MarkOnMediaEnum = UpdatePrivacyRequestRequest.MarkOnMediaEnum || (UpdatePrivacyRequestRequest.MarkOnMediaEnum = {}));
  let MessageMeEnum;
  (function (MessageMeEnum) {
    MessageMeEnum["Nobody"] = "nobody";
    MessageMeEnum["Anybody"] = "anybody";
    MessageMeEnum["ThoseFollowMe"] = "those_follow_me";
    MessageMeEnum["WhoIFollow"] = "who_i_follow";
  })(MessageMeEnum = UpdatePrivacyRequestRequest.MessageMeEnum || (UpdatePrivacyRequestRequest.MessageMeEnum = {}));
  let LocationFindMeEnum;
  (function (LocationFindMeEnum) {
    LocationFindMeEnum["Nobody"] = "nobody";
    LocationFindMeEnum["Anybody"] = "anybody";
    LocationFindMeEnum["ThoseFollowMe"] = "those_follow_me";
    LocationFindMeEnum["WhoIFollow"] = "who_i_follow";
  })(LocationFindMeEnum = UpdatePrivacyRequestRequest.LocationFindMeEnum || (UpdatePrivacyRequestRequest.LocationFindMeEnum = {}));
  let LocationShowEnum;
  (function (LocationShowEnum) {
    LocationShowEnum["DontShow"] = "dont_show";
    LocationShowEnum["Country"] = "country";
    LocationShowEnum["CountryAndCity"] = "country_and_city";
  })(LocationShowEnum = UpdatePrivacyRequestRequest.LocationShowEnum || (UpdatePrivacyRequestRequest.LocationShowEnum = {}));
})(UpdatePrivacyRequestRequest || (UpdatePrivacyRequestRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var UserCommonApplicationProfileSettings;
(function (UserCommonApplicationProfileSettings) {
  let CommunityApplicationTypeEnum;
  (function (CommunityApplicationTypeEnum) {
    CommunityApplicationTypeEnum["LanguageExchange"] = "language_exchange";
    CommunityApplicationTypeEnum["BusinessNetworking"] = "business_networking";
    CommunityApplicationTypeEnum["Dating"] = "dating";
    CommunityApplicationTypeEnum["Garden"] = "garden";
  })(CommunityApplicationTypeEnum = UserCommonApplicationProfileSettings.CommunityApplicationTypeEnum || (UserCommonApplicationProfileSettings.CommunityApplicationTypeEnum = {}));
})(UserCommonApplicationProfileSettings || (UserCommonApplicationProfileSettings = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

class ApiModule {
  static forRoot(configurationFactory) {
    return {
      ngModule: ApiModule,
      providers: [{
        provide: Configuration,
        useFactory: configurationFactory
      }]
    };
  }
  constructor(parentModule, http) {
    if (parentModule) {
      throw new Error('ApiModule is already loaded. Import in your base AppModule only.');
    }
    if (!http) {
      throw new Error('You need to import the HttpClientModule in your AppModule! \n' + 'See also https://github.com/angular/angular/issues/20575');
    }
  }
  static ɵfac = function ApiModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ApiModule)(i0.ɵɵinject(ApiModule, 12), i0.ɵɵinject(i1.HttpClient, 8));
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: ApiModule
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ApiModule, [{
    type: NgModule,
    args: [{
      imports: [],
      declarations: [],
      exports: [],
      providers: []
    }]
  }], () => [{
    type: ApiModule,
    decorators: [{
      type: Optional
    }, {
      type: SkipSelf
    }]
  }, {
    type: i1.HttpClient,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/*
 * Public API Surface of user-api
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APIS, AddressBookModel, AddressBookRequest, ApiModule, ApiService, AuthService, BASE_PATH, COLLECTION_FORMATS, ChangeProfileRequestRequest, CommonApplicationProfileSettingsLocation, CommonShortUser, Configuration, LoginResponse, LoginWithSocialMediaRequest, MainProfileService, PostUserCommonApplicationProfileSettingsRequest, Privacy, ProfileSettings, ProfileSettingsLocation, PublicProfile, RegistrationRequest, UpdatePrivacyRequestRequest, UserCommonApplicationProfileSettings, UserService, UsersService };
